import { FC, PropsWithChildren, useMemo } from "react";
import { FormControlLabel, Radio, SvgIcon, SvgIconProps, useRadioGroup } from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { RadioButtonProps } from "./radio-button.types";
import { grey } from "@mui/material/colors";

export function RadioButtonCheckIcon(props: SvgIconProps) {
  return (
    <SvgIcon {...props}>
      <circle cx="10.5" cy="10.5" r="10.5" fill={props.fill} />
      <path
        d="M6.51886 11.8908L8.33487 14.2263C8.39596 14.3048 8.4739 14.3686 8.56294 14.4129C8.65198 14.4573 8.74984 14.4811 8.8493 14.4825C8.94877 14.484 9.04729 14.4631 9.1376 14.4214C9.22791 14.3797 9.30769 14.3182 9.37107 14.2415L15.1539 7.24121"
        stroke="white"
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </SvgIcon>
  );
}

const useStyles = makeStyles()((theme) => {
  const borderRadius = (theme.shape.borderRadius as number) * 1.5;

  return {
    root: {
      flexGrow: 1,
      backgroundColor: grey[100],
      color: theme.palette.text.secondary,
      borderStyle: "solid",
      borderWidth: "2px",
      borderColor: theme.palette.secondary.main,
      borderRightStyle: "none",
      marginLeft: 0,
      marginRight: 0,
      paddingRight: theme.spacing(2),
      "&:first-of-type": {
        borderTopLeftRadius: borderRadius,
        borderBottomLeftRadius: borderRadius
      },
      "&:last-child": {
        borderTopRightRadius: borderRadius,
        borderBottomRightRadius: borderRadius,
        borderRightStyle: `solid`
      },
      "&$checked": {
        backgroundColor: "transparent",
        color: theme.palette.text.primary,
        borderColor: theme.palette.primary.main,
        "&$disabled": {
          borderColor: theme.palette.secondary.main
        }
      }
    },
    checked: {
      "& + $root": {
        borderLeftStyle: "none"
      }
    },
    disabled: {}
  };
});

export const RadioButton: FC<PropsWithChildren<RadioButtonProps>> = ({
  value,
  label,
  onChange,
  name,
  disabled
}) => {
  const radioGroup = useRadioGroup();
  const checked = radioGroup?.value === value;
  const { classes } = useStyles();
  const control = useMemo(
    () => <Radio color={checked ? "primary" : "default"} checkedIcon={<RadioButtonCheckIcon />} />,
    [checked]
  );
  return (
    <FormControlLabel
      value={value}
      control={control}
      label={label}
      classes={{
        root: classes.root,
        disabled: classes.disabled
      }}
      className={checked ? classes.checked : undefined}
      onChange={onChange}
      name={name}
      disabled={disabled}
    />
  );
};
